import React, { useState, useEffect, Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import Layout from './components/Layout';
import AppShell from './components/AppShell';
import { onAuthStateChanged, getAuth } from 'firebase/auth';
import { signInWithGoogle, logoutUser, checkUserProStatus, completeGoogleRedirectIfNeeded } from './utils/firebase';
import { IS_NATIVE } from './utils/capacitor';
import './App.css';
import { ToastProvider, useToast } from './components/ToastProvider';
import { LanguageProvider } from './utils/i18n';
import { Loader2 } from 'lucide-react';


// ✅ Lazy Imports
const ProfilePage = lazy(() => import('./pages/ProfilePage'));
const AppLoginPage = lazy(() => import('./pages/AppLoginPage'));
const PlaylistDetailsPage = lazy(() => import('./pages/PlaylistDetailsPage'));
const LandingPage = lazy(() => import('./pages/LandingPage'));
const PlayerPage = lazy(() => import('./pages/PlayerPage'));
const PrivacyPage = lazy(() => import('./pages/PrivacyPage'));
const TermsPage = lazy(() => import('./pages/TermsPage'));
const RefundPolicyPage = lazy(() => import('./pages/RefundPolicyPage'));
const FAQPage = lazy(() => import('./pages/FAQPage'));
const CookiePolicyPage = lazy(() => import('./pages/CookiePolicyPage'));
const GuidesPage = lazy(() => import('./pages/GuidesPage'));
const ArticlePage = lazy(() => import('./pages/ArticlePage'));
const AboutPage = lazy(() => import('./pages/AboutPage'));
const M3UPlayerPage = lazy(() => import('./pages/M3UPlayerPage'));
const CastingPage = lazy(() => import('./pages/CastingPage'));
const ToolsPage = lazy(() => import('./pages/ToolsPage'));
const AdminPage = lazy(() => import('./pages/AdminPage'));

const WatchRedirect = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  useEffect(() => {
    const url = searchParams.get('url');
    const name = searchParams.get('name') || 'Shared Stream';
    if (url) {
      navigate('/player', { replace: true, state: { streamUrl: url, name } });
    } else {
      navigate('/', { replace: true });
    }
  }, []);

  return <FullScreenLoader text="Loading stream..." />;
};

const CanonicalHandler = () => {
  const location = useLocation();

  useEffect(() => {
    const path = location.pathname === '/' ? '' : location.pathname;
    const canonicalUrl = `https://www.webiptvplayer.com${path}`;

    let link = document.querySelector("link[rel='canonical']");
    if (!link) {
      link = document.createElement('link');
      link.setAttribute('rel', 'canonical');
      document.head.appendChild(link);
    }

  }, [location]);

  return null;
};

const AnalyticsTracker = () => {
  const location = useLocation();

  useEffect(() => {
    if (typeof window.gtag === 'function') {
      window.gtag('event', 'page_view', {
        page_path: location.pathname + location.search,
        page_location: window.location.href,
        page_title: document.title
      });
    }
  }, [location]);

  return null;
};


// ✅ FIXED: Truly Full Screen Loader (Covers old background immediately)
const FullScreenLoader = ({ text = "Loading..." }) => (
  <div style={{ 
    position: 'fixed', // Forces it to cover the screen
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 9999,
    background: '#0b0b0b', // Dark background to prevent white flash
    display: 'flex', 
    alignItems: 'center', 
    justifyContent: 'center',
    flexDirection: 'column',
    gap: '15px'
  }}>
    <Loader2 className="animate-spin" size={40} color="#3b82f6" />
    <span style={{ color: '#888', fontFamily: 'sans-serif', fontSize: '0.9rem', letterSpacing: '0.5px' }}>
      {text}
    </span>
  </div>
);

const AppContent = () => {
  const { addToast } = useToast();
  const [user, setUser] = useState(null);
  const [isPro, setIsPro] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  
  const [authLoading, setAuthLoading] = useState(true);
  const [redirectChecked, setRedirectChecked] = useState(false);

  const handleSignIn = async () => {
    try {
      const loggedInUser = await signInWithGoogle(addToast);
      return loggedInUser;
    } catch (err) {
      console.error('Google sign-in handler failed:', err);
      addToast('Login failed. Please try again.', 'error');
      return null;
    }
  };

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    let active = true;

    completeGoogleRedirectIfNeeded()
      .catch((err) => {
        console.warn('Redirect completion failed or not present.', err);
      })
      .finally(() => {
        if (active) setRedirectChecked(true);
      });

    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    const auth = getAuth();
    
    // Safety Timeout
    const safetyTimer = setTimeout(() => {
       if (authLoading) {
          console.warn("Auth check timed out. Letting user in.");
          setAuthLoading(false);
       }
    }, 4000);

    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      setUser(currentUser);
      if (currentUser) {
        try {
            const proStatus = await checkUserProStatus(currentUser.uid);
            setIsPro(proStatus);
        } catch (e) {
            console.error("Pro check failed", e);
            setIsPro(false);
        }
      } else {
        setIsPro(false);
      }
      
      clearTimeout(safetyTimer);
      setAuthLoading(false); 
    });
    return () => {
        unsubscribe();
        clearTimeout(safetyTimer);
    };
  }, []);

  // Show Loader for Auth Check
  if (authLoading || !redirectChecked) {
    return <FullScreenLoader text="Initializing..." />;
  }

  return (
    <Router>
      <CanonicalHandler />
      <AnalyticsTracker />
      <Routes>
        {/* Watch/Share Route */}
        <Route path="/watch" element={<WatchRedirect />} />

        {/* Player Route */}
        <Route
          path="/player"
          element={
            <Suspense fallback={<FullScreenLoader text="Loading Player..." />}>
              <PlayerPage user={user} isPro={isPro} authLoading={authLoading} />
            </Suspense>
          } 
        />
        
        {/* ── NATIVE APP ROUTES (Capacitor) ── */}
        {IS_NATIVE ? (
          <Route element={<AppShell user={user} isPro={isPro} signInWithGoogle={handleSignIn} logoutUser={logoutUser} />}>
            <Route
              path="/"
              element={
                <Suspense fallback={<FullScreenLoader />}>
                  {user
                    ? <ProfilePage />
                    : <AppLoginPage signInWithGoogle={handleSignIn} />
                  }
                </Suspense>
              }
            />
            <Route path="/profile" element={<Suspense fallback={<FullScreenLoader />}><ProfilePage /></Suspense>} />
            <Route path="/playlist" element={<Suspense fallback={<FullScreenLoader />}><PlaylistDetailsPage /></Suspense>} />
          </Route>
        ) : (
        /* ── WEB ROUTES (Browser) ── */
        <Route element={<Layout user={user} isPro={isPro} isMobile={isMobile} signInWithGoogle={handleSignIn} logoutUser={logoutUser} />}>
          <Route path="/" element={<Suspense fallback={<FullScreenLoader />}><LandingPage user={user} isPro={isPro} isMobile={isMobile} /></Suspense>} />
          <Route path="/profile" element={<Suspense fallback={<FullScreenLoader />}><ProfilePage /></Suspense>} />
          <Route path="/playlist" element={<Suspense fallback={<FullScreenLoader />}><PlaylistDetailsPage /></Suspense>} />
          <Route path="/privacy" element={<Suspense fallback={<FullScreenLoader />}><PrivacyPage /></Suspense>} />
          <Route path="/terms" element={<Suspense fallback={<FullScreenLoader />}><TermsPage /></Suspense>} />
          <Route path="/refund" element={<Suspense fallback={<FullScreenLoader />}><RefundPolicyPage /></Suspense>} />
          <Route path="/faq" element={<Suspense fallback={<FullScreenLoader />}><FAQPage isPro={isPro} /></Suspense>} />
          <Route path="/cookies" element={<Suspense fallback={<FullScreenLoader />}><CookiePolicyPage /></Suspense>} />
          <Route path="/about" element={<Suspense fallback={<FullScreenLoader />}><AboutPage /></Suspense>} />
          <Route path="/m3u-player" element={<Suspense fallback={<FullScreenLoader />}><M3UPlayerPage isPro={isPro} /></Suspense>} />
          <Route path="/chromecast-iptv" element={<Suspense fallback={<FullScreenLoader />}><CastingPage /></Suspense>} />
          <Route path="/guides" element={<Suspense fallback={<FullScreenLoader />}><GuidesPage isPro={isPro} /></Suspense>} />
          <Route path="/guides/:id" element={<Suspense fallback={<FullScreenLoader />}><ArticlePage isPro={isPro} /></Suspense>} />
          <Route path="/tools" element={<Suspense fallback={<FullScreenLoader />}><ToolsPage /></Suspense>} />
          <Route path="/admin" element={<Suspense fallback={<FullScreenLoader />}><AdminPage /></Suspense>} />
        </Route>
        )}
      </Routes>
    </Router>
  );
}
function App() {
  return (
    <LanguageProvider>
      <ToastProvider>
        <AppContent />
      </ToastProvider>
    </LanguageProvider>
  );
}

export default App;

